import { useState } from 'react';

import {
    ReviewUnmatchedProductsRequest,
    UnmatchedVesselProductMessage,
} from '../../../../types/UnmatchedVesselProductMessage';
import { Button } from '../../../cdl/Button/Button';
import { Modal } from '../../../cdl/Modal/Modal';
import { Text } from '../../../cdl/Text/Text';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { ProductOption, ProductSelect } from '../../../common/form-elements/ProductSelect/ProductSelect';
import { translate } from '../../../common/helpers/translate.helper';
import { IconArrowNarrowRight } from '../../../common/icons/cdl/ArrowNarrowRight';
import { Box } from '../../../common/ui/Box';

interface ReviewUnmatchedProductsModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    unmatchedProducts: UnmatchedVesselProductMessage[];
    vesselId: string;
    onSubmit: (request: ReviewUnmatchedProductsRequest) => Promise<void>;
    isLoading: boolean;
}

export const ReviewUnmatchedProductsModal = ({
    isOpen,
    onDismiss,
    unmatchedProducts,
    vesselId,
    onSubmit,
    isLoading,
}: ReviewUnmatchedProductsModalProps) => {
    const [selections, setSelections] = useState<Record<string, string>>({});

    const handleDismiss = () => {
        setSelections({});
        onDismiss();
    };

    const handleProductChange = (unmatchedVesselProductId: string, option: ProductOption | null) => {
        setSelections((prev) => ({
            ...prev,
            [unmatchedVesselProductId]: option?.value ?? '',
        }));
    };

    const handleSubmit = async () => {
        const resolutions = Object.entries(selections)
            .filter(([, productId]) => productId)
            .map(([unmatchedVesselProductId, productId]) => ({
                unmatchedVesselProductId,
                productId,
            }));

        const resolvedIds = new Set(resolutions.map((r) => r.unmatchedVesselProductId));
        const dismissedProductIds = unmatchedProducts.filter((p) => !resolvedIds.has(p.id)).map((p) => p.id);

        await onSubmit({ resolutions, dismissedProductIds });
        handleDismiss();
    };

    const handleMarkAllReviewed = async () => {
        const dismissedProductIds = unmatchedProducts.map((p) => p.id);
        await onSubmit({ resolutions: [], dismissedProductIds });
        handleDismiss();
    };

    return (
        <Modal isOpen={isOpen} onDismiss={handleDismiss} size="large">
            <Box padding={24} display="grid" rowGap={24}>
                <Box display="grid" rowGap={8}>
                    <Text variant="title">{translate('vessel.products.unmatchedModal.title')}</Text>
                    <Text color="foreground.muted">{translate('vessel.products.unmatchedModal.description')}</Text>
                </Box>

                <Box display="grid" rowGap={4}>
                    <Box display="grid" gridTemplateColumns="1fr auto 1fr" columnGap={16} paddingBottom={8}>
                        <Text variant="small" color="foreground.default">
                            {translate('vessel.products.unmatchedModal.unmatchedItem')}
                        </Text>
                        <Box width={16} />
                        <Text variant="small" color="foreground.default">
                            {translate('vessel.products.unmatchedModal.product')}
                        </Text>
                    </Box>

                    <Box display="grid" rowGap={16}>
                        {unmatchedProducts.map((product) => {
                            const hasSelection = Boolean(selections[product.id]);

                            return (
                                <Box
                                    key={product.id}
                                    display="grid"
                                    gridTemplateColumns="1fr auto 1fr"
                                    columnGap={16}
                                    alignItems="center"
                                >
                                    <Text color={hasSelection ? 'foreground.subtle' : 'foreground.default'}>
                                        {product.productString}
                                    </Text>
                                    <IconArrowNarrowRight width={16} height={16} />
                                    <ProductSelect
                                        value={selections[product.id] || ''}
                                        onChange={(option) => handleProductChange(product.id, option)}
                                        placeholder={translate('vessel.products.unmatchedModal.selectProduct')}
                                        vesselId={vesselId}
                                        hideSuccessorProduct
                                        isClearable
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box display="flex" justifyContent="space-between">
                    <Button onClick={handleMarkAllReviewed} isLoading={isLoading}>
                        {translate('vessel.products.unmatchedModal.markAllReviewed')}
                    </Button>
                    <ButtonGroup>
                        <Button onClick={handleDismiss} type="button">
                            {translate('actions.cancel')}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            emphasis="high"
                            isLoading={isLoading}
                            disabled={!Object.values(selections).some(Boolean)}
                        >
                            {translate('vessel.products.unmatchedModal.submit')}
                        </Button>
                    </ButtonGroup>
                </Box>
            </Box>
        </Modal>
    );
};
